import Vue from 'vue'
import { MutationTree } from 'vuex'
import { LessonStateInterface } from 'src/store/lesson/state'
import {
  ContentAsset,
  LessonPlan,
  ItemTypeEnum,
  ItemUser,
  Asset,
  Item,
  ItemSetting,
  User, UserId, Role, ItemId
} from 'able-lib'
import { deleteProp, setProp } from 'src/lib/helper'
import { ITEM_LESSON_PLAN_TEMPLATE, ITEM_LESSON_TEMPLATE } from 'src/lib/tools/lesson'

const mutation: MutationTree<LessonStateInterface> = {

  setLesson (state, lesson: Item): void {
    Vue.set(state, 'lesson', lesson)
    Vue.set(state, 'lessonDraft', JSON.parse(JSON.stringify(lesson)))
  },

  setLessonSettings (state, itemSettings: Array<ItemSetting>): void {
    state.lessonDraft.itemSettings = itemSettings
  },

  updateLessonSettings (state, itemSetting: ItemSetting): void {
    const settingExists = state.lessonDraft.itemSettings.find(setting => setting.key === itemSetting.key)
    if (settingExists) {
      state.lessonDraft.itemSettings.find(setting => setting.key === itemSetting.key).value = itemSetting.value
    } else {
      state.lessonDraft.itemSettings.push(itemSetting)
    }
  },

  setWatchLessonSettings (state, itemSettings: Array<ItemSetting>): void {
    state.lessonSettings = itemSettings
  },

  CLONE_LESSON (state): void {
    delete state.lessonDraft.id
    delete state.lessonDraft.content.id
    state.lessonDraft.items.forEach((item: Item, itemIndex: number) => {
      delete state.lessonDraft.items[itemIndex].id
      delete state.lessonDraft.items[itemIndex].content.id
      state.lessonDraft.items[itemIndex].content.contentAssets.forEach((contentAsset: ContentAsset, contentAssetIndex: number) => {
        if (!state.lessonDraft.items[itemIndex].content) {
          if (!state.lessonDraft.items[itemIndex].content.contentAssets[contentAssetIndex].asset.isShared) {
            delete state.lessonDraft.items[itemIndex].content.contentAssets[contentAssetIndex].asset.id
          }
        }
      })
    })
  },

  updateOriginalItem (state, item: Item) {
    state.lessonDraft.originalItem = item
  },

  setItemUser (state, users: Array<ItemUser>): void {
    state.lessonDraft.itemUsers = users
  },

  removeTeacher (state, userId) {
    state.lessonDraft.itemUsers.forEach((itemUser, index) => {
      if (itemUser.user.id === userId) state.lessonDraft.itemUsers.splice(index, 1)
    })
  },

  addItemUser (state: LessonStateInterface, { user, role }: { user: User; role: Role }): void {
    const duplicateUser = state.lessonDraft.itemUsers.find((itemUser: ItemUser) => itemUser.user.id === user.id)

    if (!duplicateUser) {
      state.lessonDraft.itemUsers.push({
        user,
        role
      })
    }
  },

  removeUser (state: LessonStateInterface, userId: UserId): void {
    const userIndex = state.lessonDraft.itemUsers.findIndex((itemUser: ItemUser) => itemUser.user.id === userId)
    state.lessonDraft.itemUsers.splice(userIndex, 1)
  },

  updateLessonPlan (state, lessonPlan: LessonPlan) {
    state.lessonDraft.originalItem.lessonPlan = lessonPlan
  },

  setMaterials (state, materials: Array<ContentAsset>): void {
    state.library.materials = materials
  },

  addLessonItem (state) {
    if (!state.lessonDraft.items) state.lessonDraft.items = []
    state.lessonDraft.items.push({
      type: ItemTypeEnum.CONTENT,
      order: state.lessonDraft.items.length.toString(),
      content: {
        contentAssets: [],
        title: '',
        body: '',
        annotation: {}
      },
      itemUsers: [],
      itemGroups: []
    })
  },

  addAssignmentLessonItem (state) {
    if (!state.lessonDraft.items) state.lessonDraft.items = []
    state.lessonDraft.items.push({
      type: ItemTypeEnum.ASSIGNMENT,
      order: state.lessonDraft.items.length.toString(),
      content: {
        contentAssets: [],
        title: '',
        body: '',
        annotation: {}
      },
      itemUsers: [],
      itemGroups: []
    })
  },

  removeLessonItem (state, itemIndex: number) {
    state.lessonDraft.items.splice(itemIndex, 1)
  },

  updateLessonItemTitle (state, { itemIndex, title }: { itemIndex: number; title: string }) {
    state.lessonDraft.items[itemIndex].content.title = title
  },

  updateLessonItemContent (state, { itemIndex, content }: { itemIndex: number; content: string }) {
    state.lessonDraft.items[itemIndex].content.body = content
  },

  updateLessonItemUsers (state, { itemIndex, itemUsers }: { itemIndex: number; itemUsers: Array<ItemUser> }) {
    state.lessonDraft.items[itemIndex].itemUsers = itemUsers
  },

  removeLessonItemUser (state, { itemIndex, itemUserIndex }: { itemIndex: number; itemUserIndex: number }) {
    state.lessonDraft.items[itemIndex].itemUsers.splice(itemUserIndex, 1)
  },

  updateLessonItemMaterials (state, { itemIndex, itemMaterials }: { itemIndex: number; itemMaterials: Array<ContentAsset> }) {
    state.lessonDraft.items[itemIndex].content.contentAssets = itemMaterials
  },

  addLessonItemMaterial (state, { itemIndex, material }: { itemIndex: number; material: Asset }) {
    state.lessonDraft.items[itemIndex].content.contentAssets.push({
      asset: material
    })
  },

  removeLessonItemMaterial (state, { itemIndex, itemMaterialIndex }: { itemIndex: number; itemMaterialIndex: number }) {
    state.lessonDraft.items[itemIndex].content.contentAssets.splice(itemMaterialIndex, 1)
  },

  toggleNote (state, value: boolean): void {
    state.lessonControls.openNote = value
  },

  toggleChat (state, value: boolean): void {
    state.lessonControls.openChat = value
  },

  SET_ITEM_LESSON_DRAFT (state: LessonStateInterface, lesson: Item) {
    state.lessonDraft = lesson
  },

  RESET_LESSON_DRAFT (state: LessonStateInterface) {
    state.lessonDraft = ITEM_LESSON_TEMPLATE
  },

  SET_ITEM_LESSON_PROP (state: LessonStateInterface, { path, value }: { path: Array<string>; value: any }) {
    console.log('SET_ITEM_LESSON_PROP', path)
    setProp(state.lessonDraft, path, value)
  },

  REMOVE_ITEM_LESSON_PROP (state: LessonStateInterface, path: Array<string>) {
    deleteProp(state.lessonDraft, path)
  },

  SET_ITEM_LESSON_PLAN_LIST (state: LessonStateInterface, lessonPlans: Array<Item>) {
    state.lessonPlanList = lessonPlans
  },

  ADD_LESSON_PLAN_ON_LIST (state: LessonStateInterface, newLessonPlan: Item) {
    state.lessonPlanList.push(newLessonPlan)
  },

  UPDATE_LESSON_PLAN_ON_LIST (state: LessonStateInterface, updatedLessonPlan: Item) {
    const lessonPlanIndex = state.lessonPlanList.findIndex(lessonPlan => lessonPlan.id === updatedLessonPlan.id)
    Vue.set(state.lessonPlanList, lessonPlanIndex + '', updatedLessonPlan)
  },

  REMOVE_LESSON_PLAN_ON_LIST_BY_ID (state: LessonStateInterface, lessonPlanId: ItemId) {
    const lessonPlanIndex = state.lessonPlanList.findIndex(lessonPlan => lessonPlan.id === lessonPlanId)
    state.lessonPlanList.splice(lessonPlanIndex, 1)
  },

  RESET_LESSON_PLAN_DRAFT (state: LessonStateInterface) {
    state.lessonPlanDraft = ITEM_LESSON_PLAN_TEMPLATE
  },

  SET_ITEM_LESSON_PLAN_DRAFT (state: LessonStateInterface, lessonPlan: Item) {
    state.lessonPlanDraft = lessonPlan
  },

  ADD_ITEM_LESSON_PLAN_ACTIVITY (state: LessonStateInterface, item: Item) {
    state.lessonPlanDraft.items.push(item)
  },

  SET_ITEM_LESSON_PLAN_PROP (state: LessonStateInterface, { path, value }: { path: Array<string>; value: any }) {
    console.log('SET_ITEM_LESSON_PLAN_PROP', path)
    setProp(state.lessonPlanDraft, path, value)
  },

  REMOVE_ITEM_LESSON_PLAN_PROP (state: LessonStateInterface, path: Array<string>) {
    deleteProp(state.lessonPlanDraft, path)
  },

  UPDATE_ITEM_ACTIVITIES_ORDER_FILLED (state: LessonStateInterface, lessonTypeDraft) {
    for (const key in state[lessonTypeDraft].items) {
      state[lessonTypeDraft].items[key].order = key
    }
  },

  ADD_INHERITED_ACTIVITY (state: LessonStateInterface, activity: Item) {
    state.inheritedActivities.push(activity)
  },

  REMOVE_INHERITED_ACTIVITY (state: LessonStateInterface, removedActivityId: ItemId) {
    const foundIndex = state.inheritedActivities.findIndex(activity => {
      return activity.id === removedActivityId
    })
    console.log('REMOVE_INHERITED_ACTIVITY', foundIndex)
    state.inheritedActivities.splice(foundIndex, 1)
  }
}

export default mutation
